import Badge from "@/admin/experiences/badges/Badge"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { ProductAppKind } from "@/dashboard/util/__generated__/useCanEditDashboardFragment.graphql"
import Relay from "@/relay/relayUtils"
import { AppFilterDropdownContentQuery } from "@components/dropdown/app/__generated__/AppFilterDropdownContentQuery.graphql"
import { SelectOption } from "@disco-ui"
import DiscoPaginatedDropdownContent, {
  DiscoPaginatedDropdownContentSkeleton,
} from "@disco-ui/dropdown/DiscoPaginatedDropdownContent"
import { TestIDProps } from "@utils/typeUtils"
import { useMemo } from "react"
import { graphql } from "relay-runtime"

const APPS_PER_LOAD = 10

type AppFilterOption = SelectOption<string>

export type AppFilterDropdownContentState = {
  allApps: AppFilterOption | null
  apps: AppFilterOption[]
}

export interface AppFilterDropdownContentProps extends TestIDProps {
  filterState: AppFilterDropdownContentState
  setFilterState: (state: AppFilterDropdownContentState) => void
  kind: ProductAppKind
  multiSelect?: boolean
  includeNested?: boolean
}

function AppFilterDropdownContent({
  testid = "AppFilterDropdownContent",
  filterState,
  setFilterState,
  kind,
  multiSelect = false,
  includeNested = true,
}: AppFilterDropdownContentProps) {
  const activeOrganization = useActiveOrganization()
  const selectedOptions = useMemo(() => {
    return [...(filterState.allApps ? [filterState.allApps] : []), ...filterState.apps]
  }, [filterState.allApps, filterState.apps])

  const { data, pagination } =
    Relay.useRefetchablePaginationQuery<AppFilterDropdownContentQuery>(
      graphql`
        query AppFilterDropdownContentQuery(
          $id: ID!
          $kind: ProductAppKind!
          $includeNested: Boolean
          $first: Int!
          $search: String
          $after: String
        ) {
          organization: node(id: $id) {
            ... on Organization {
              apps(
                first: $first
                after: $after
                search: $search
                kind: $kind
                includeNested: $includeNested
              ) @connection(key: "AppFilterDropdownContent__apps") {
                edges {
                  node {
                    id
                    customAppTitle
                    badge {
                      ...BadgeFragment
                    }
                    navSection {
                      title
                    }
                  }
                }
                pageInfo {
                  startCursor
                  endCursor
                  hasNextPage
                  hasPreviousPage
                }
              }
            }
          }
        }
      `,
      {
        id: activeOrganization?.id,
        first: APPS_PER_LOAD,
        kind,
        includeNested,
      },
      { connectionName: "organization.apps", fetchPolicy: "network-only" }
    )

  const organization = data?.organization

  const apps = Relay.connectionToArray(organization?.apps)
  const options = [
    { value: "all", title: "All", testid: "apps-all" },
    ...apps.map((app) => ({
      value: app.id,
      title: app.customAppTitle || "",
      subtitle: app.navSection?.title || "",
      icon: <Badge badgeKey={app.badge} size={24} />,
      testid: `app-${app.customAppTitle || app.id}`,
    })),
  ]

  if (!organization) return null

  return (
    <DiscoPaginatedDropdownContent
      testid={testid}
      pagination={pagination}
      selectedOptions={selectedOptions}
      options={options}
      onOptionSelect={handleSelect}
    />
  )

  function handleSelect(option: AppFilterOption | null) {
    const state: AppFilterDropdownContentState = {
      allApps: null,
      apps: [],
    }

    if (option?.value === "all") {
      state.allApps = option
    } else if (option) {
      state.apps = multiSelect ? filterState.apps?.concat(option) ?? [option] : [option]
    }

    setFilterState(state)
  }
}

export const AppFilterDropdownContentSkeleton = () => {
  return <DiscoPaginatedDropdownContentSkeleton />
}

export default Relay.withSkeleton({
  component: AppFilterDropdownContent,
  skeleton: AppFilterDropdownContentSkeleton,
})
